import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert } from "@/supabase/types"
import { TableType } from "@/types/base/enums"
import { PermAction, PermOptions } from '@/types/base/permissions'
import { Role } from "@/types/base/authuser"
import Authuser from '@/types/base/authuser'
import { getGroupById } from "./groups"
import { getRoleById } from "./roles"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'

const logger = createIsomorphicLogger('db.groupRoles')

export const getGroupRoleById = async (supabase: SupabaseClient<Database>, id: string) => {
  const { data: groupRole, error } = await supabase
    .from("group_roles")
    .select("*")
    .eq("id", id)
    .single()

  if (!groupRole) {
    logError(logger, error, 'Failed to fetch Group Role', { id })
    throw new Error(error.message)
  }

  return groupRole
}

export const getGroupRolesByGroupId = async (
	supabase: SupabaseClient<Database>, 
  groupId: string
) => {
  const { data: groupRoles, error } = await supabase
    .from("groups")
    .select(
      `
        id, 
        name, 
        roles ( * )
      `
    )
    .eq("id", groupId)
    .single()

  if (!groupRoles) {
    logError(logger, error, 'Failed to fetch Group Roles by Group ID', { groupId })
    throw new Error(error.message)
  }

  return groupRoles
}

export const createGroupRole = async (
	supabase: SupabaseClient<Database>, 
  groupRole: TablesInsert<"group_roles">
) => {
  const { data: createdGroupRole, error } = await supabase
    .from("group_roles")
    .insert(groupRole)
    .select("*")

  if (!createdGroupRole) {
    logError(logger, error, 'Failed to create Group Role', { groupRole: printObject(groupRole) })
    throw new Error(error.message)
  }

  logger.info('Group Role created successfully', { groupRole: printObject(createdGroupRole) })
  return createdGroupRole
}

export const createGroupRoles = async (
	supabase: SupabaseClient<Database>, 
  groupRoles: TablesInsert<"group_roles">[]
) => {
  const { data: createdGroupRoles, error } = await supabase
    .from("group_roles")
    .insert(groupRoles)
    .select("*")

  if (!createdGroupRoles) {
    logError(logger, error, 'Failed to create Group Roles', { groupRoles: printObject(groupRoles) })
    throw new Error(error.message)
  }

  logger.info('Group Roles created successfully', { groupRoles: printObject(createdGroupRoles) })
  return createdGroupRoles
}

export const deleteGroupRole = async (
	supabase: SupabaseClient<Database>, 
  groupId: string,
  roleId: string
) => {
  const { error } = await supabase
    .from("group_roles")
    .delete()
    .eq("group_id", groupId)
    .eq("role_id", roleId)

  if (error) {
    logError(logger, error, 'Failed to delete Group Role', { groupId, roleId })
    throw new Error(error.message)
  }
  
  return true
}

export const groupRolesCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	switch (options.action) {
		case PermAction.select:
			if (options.relUuid) {
				const authorized = await authuser.isAuthorizedForTable(supabase, TableType.group, options.relUuid, Role.admin)

				if (options.uuid) {
					const group = await getGroupById(supabase, options.relUuid) 
					return !authuser.isGenerated(group.sharing)
				}
			}
			return false  //we need an id to confirm in this case
		default:
			return authuser.isAuthorizedForRole(Role.admin)
	}
}